/**
 * Music engine — Suno-generated MP3 tracks per zone, with crossfade
 * between zone changes. Falls back to procedural Web Audio pads when
 * the MP3 hasn't loaded or 404s.
 *
 * Tracks live in public/music/ and are picked via setMusicZone().
 */

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let zoneGain: GainNode | null = null;
let started = false;
let muted = false;
let currentZone: ZoneKey = 'overworld';
let scheduler: number | null = null;

type ZoneKey = 'overworld' | 'tidal' | 'rooted' | 'anchorless' | 'combat' | 'menu';

/** Map zone key → track(s). Paths starting with `audio-sirus/` resolve from
 *  the site root (Sirus 1:1 originals on the hive); bare filenames resolve
 *  under public/music/ as before. Arrays are shuffled playlists — tracks
 *  chain via `ended` instead of looping one file forever. */
const SIRUS_FOREST_DAY = [
  'audio-sirus/Sound/Music/ZoneMusic/Forest/DayForest01.mp3',
  'audio-sirus/Sound/Music/ZoneMusic/Forest/DayForest02.mp3',
  'audio-sirus/Sound/Music/ZoneMusic/Forest/DayForest03.mp3',
];
const ZONE_TRACKS: Record<ZoneKey, string | string[]> = {
  menu:       'audio-sirus/Sound/Music/GlueScreenMusic/wow_main_theme.mp3',
  overworld:  SIRUS_FOREST_DAY,
  tidal:      SIRUS_FOREST_DAY,
  rooted:     SIRUS_FOREST_DAY,
  anchorless: SIRUS_FOREST_DAY,
  combat:     'combat-theme.mp3',
};

let currentAudio: HTMLAudioElement | null = null;
let nextAudio: HTMLAudioElement | null = null;
let crossfadeTimer: number | null = null;
const FADE_MS = 1500;
const BASE_VOL = 0.45;
let musicVolume = 1.0;
let masterVolume = 1.0;

function effectiveVol(): number {
  if (muted) return 0;
  return BASE_VOL * musicVolume * masterVolume;
}

export function setMusicVolume(v: number): void {
  musicVolume = Math.max(0, Math.min(1, v));
  if (currentAudio) currentAudio.volume = effectiveVol();
}

export function setMasterVolumeForMusic(v: number): void {
  masterVolume = Math.max(0, Math.min(1, v));
  if (currentAudio) currentAudio.volume = effectiveVol();
}

interface ZoneTune {
  root: number; // Hz of the root note (A2 ≈ 110)
  chords: number[][]; // each chord is a list of semitone offsets from root
  beatSec: number;
  padOctaves: [number, number]; // pad register (in octaves above root)
  melodyChance: number;
}

const ZONES: Record<ZoneKey, ZoneTune> = {
  overworld:  { root: 110.0, chords: [[0,3,7], [-2,5,7], [-3,4,7], [-5,2,7]],   beatSec: 1.4, padOctaves: [1, 2], melodyChance: 0.35 },
  tidal:      { root: 98.0,  chords: [[0,3,7,10], [-2,5,7], [-5,3,7], [-7,5,10]], beatSec: 1.6, padOctaves: [1, 2], melodyChance: 0.32 },
  rooted:     { root: 130.8, chords: [[0,4,7], [5,9,12], [-5,2,7], [0,4,7]],     beatSec: 1.2, padOctaves: [0, 1], melodyChance: 0.3 },
  anchorless: { root: 116.5, chords: [[0,3,7,10], [3,6,10], [-2,5,8], [-5,2,7]], beatSec: 1.1, padOctaves: [1, 2], melodyChance: 0.45 },
  combat:     { root: 92.5,  chords: [[0,3,6,10], [-2,3,5,8], [-3,3,7,10]],      beatSec: 0.8, padOctaves: [0, 1], melodyChance: 0.6 },
  menu:       { root: 130.8, chords: [[0,4,7,11], [5,9,12], [-5,3,7], [0,4,7]],   beatSec: 1.5, padOctaves: [1, 2], melodyChance: 0.4 },
};

function ensureCtx(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const W = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
    const Ctor = W.AudioContext ?? W.webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = muted ? 0 : 0.18;
    master.connect(ctx.destination);
    zoneGain = ctx.createGain();
    zoneGain.gain.value = 1;
    zoneGain.connect(master);
  } catch {
    return null;
  }
  return ctx;
}

function semitonesToHz(rootHz: number, semitones: number): number {
  return rootHz * Math.pow(2, semitones / 12);
}

function playPadNote(c: AudioContext, dest: AudioNode, freq: number, durationSec: number, vol = 0.07): void {
  const now = c.currentTime;
  // 3-osc pad: sine + triangle + slightly detuned saw.
  const oscs = [
    { type: 'sine' as OscillatorType, freq, detune: 0, gain: 0.6 },
    { type: 'triangle' as OscillatorType, freq: freq * 2, detune: -3, gain: 0.25 },
    { type: 'sawtooth' as OscillatorType, freq, detune: 5, gain: 0.12 },
  ];
  const padG = c.createGain();
  padG.gain.setValueAtTime(0, now);
  padG.gain.linearRampToValueAtTime(vol, now + 0.6);
  padG.gain.linearRampToValueAtTime(vol * 0.5, now + durationSec * 0.6);
  padG.gain.linearRampToValueAtTime(0, now + durationSec);
  // Lowpass for warmth.
  const filt = c.createBiquadFilter();
  filt.type = 'lowpass';
  filt.frequency.value = 1600;
  filt.Q.value = 0.7;
  for (const o of oscs) {
    const osc = c.createOscillator();
    osc.type = o.type;
    osc.frequency.value = o.freq;
    osc.detune.value = o.detune;
    const g = c.createGain();
    g.gain.value = o.gain;
    osc.connect(g);
    g.connect(filt);
    osc.start(now);
    osc.stop(now + durationSec + 0.05);
  }
  filt.connect(padG);
  padG.connect(dest);
}

function playMelodyNote(c: AudioContext, dest: AudioNode, freq: number, durationSec: number, vol = 0.05): void {
  const now = c.currentTime;
  const osc = c.createOscillator();
  osc.type = 'triangle';
  osc.frequency.value = freq;
  const g = c.createGain();
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(vol, now + 0.04);
  g.gain.exponentialRampToValueAtTime(0.001, now + durationSec);
  osc.connect(g);
  g.connect(dest);
  osc.start(now);
  osc.stop(now + durationSec + 0.02);
}

let chordIdx = 0;

// Procedural fallback — kept around in case the MP3 fails to load.
export function _proceduralTick(): void {
  if (!ctx || !zoneGain) return;
  const tune = ZONES[currentZone];
  const chord = tune.chords[chordIdx % tune.chords.length]!;
  // Pad chord — voices at root + chord intervals at the requested octave range.
  for (const semi of chord) {
    const freq = semitonesToHz(tune.root, semi);
    playPadNote(ctx, zoneGain, freq, tune.beatSec * 4, 0.06);
    // Layer one octave up at lower volume.
    playPadNote(ctx, zoneGain, freq * 2, tune.beatSec * 4, 0.025);
  }
  // Optional melody — picked from the chord, played at a higher octave.
  if (Math.random() < tune.melodyChance) {
    const beats = 4;
    for (let i = 0; i < beats; i++) {
      const interval = chord[Math.floor(Math.random() * chord.length)]!;
      const freq = semitonesToHz(tune.root, interval) * 4;
      // Stagger via a tail-end setTimeout — Web Audio scheduling is per-call.
      window.setTimeout(() => {
        if (ctx && zoneGain) playMelodyNote(ctx, zoneGain, freq, tune.beatSec * 0.6, 0.04);
      }, i * tune.beatSec * 1000);
    }
  }
  chordIdx = (chordIdx + 1) % tune.chords.length;
}

let playlistIdx = 0;

function resolveTrackUrl(track: string): string {
  const base = import.meta.env.BASE_URL;
  return track.startsWith('audio-sirus/') ? `${base}${track}` : `${base}music/${track}`;
}

function playMp3(zone: ZoneKey, chained = false): void {
  if (typeof Audio === 'undefined') return;
  const entry = ZONE_TRACKS[zone];
  const isPlaylist = Array.isArray(entry);
  if (isPlaylist && !chained) playlistIdx = Math.floor(Math.random() * entry.length);
  const track = isPlaylist ? entry[playlistIdx % entry.length]! : entry;
  const url = resolveTrackUrl(track);

  // Crossfade — start the new audio at vol 0, ramp to target while old
  // ramps to 0, then dispose old.
  const newA = new Audio(url);
  newA.loop = !isPlaylist;
  newA.volume = 0;
  newA.preload = 'auto';
  if (isPlaylist) {
    newA.addEventListener('ended', () => {
      if (currentAudio !== newA) return; // a zone change already superseded us
      playlistIdx = (playlistIdx + 1) % entry.length;
      playMp3(zone, true);
    });
  }
  void newA.play().catch(() => { /* user-gesture gating; will retry on next gesture */ });

  const old = currentAudio;
  currentAudio = newA;
  nextAudio = null;

  if (crossfadeTimer !== null) window.clearInterval(crossfadeTimer);
  const t0 = performance.now();
  crossfadeTimer = window.setInterval(() => {
    const dt = performance.now() - t0;
    const t = Math.min(1, dt / FADE_MS);
    if (!muted) newA.volume = effectiveVol() * t;
    if (old) old.volume = effectiveVol() * (1 - t);
    if (t >= 1) {
      if (old) {
        try { old.pause(); old.src = ''; } catch {}
      }
      if (crossfadeTimer !== null) window.clearInterval(crossfadeTimer);
      crossfadeTimer = null;
    }
  }, 40);
}

export function startMusic(): void {
  if (started) return;
  started = true;
  // R232q5cq/cu: initial volumes + mute states из localStorage.
  // R232q5cu: musicMuted ИЛИ masterMuted → music silent (OR логика).
  try {
    const raw = localStorage.getItem('aiwari-settings');
    if (raw) {
      const s = JSON.parse(raw) as { masterVolume?: number; musicVolume?: number; musicMuted?: boolean; masterMuted?: boolean };
      if (typeof s.masterVolume === 'number') masterVolume = s.masterVolume;
      if (typeof s.musicVolume === 'number') musicVolume = s.musicVolume;
      muted = (s.musicMuted === true) || (s.masterMuted === true);
    }
  } catch { /* ignore */ }
  // Procedural Web Audio is now a fallback; primary path is MP3.
  playMp3(currentZone);
  // Also boot the synth ctx in case MP3 fails.
  ensureCtx();
}

export function setMusicZone(zone: ZoneKey): void {
  if (zone === currentZone) return;
  currentZone = zone;
  if (started) {
    playMp3(zone);
  }
  // Re-arm the procedural fallback in case MP3 crossfade fails.
  if (started && scheduler !== null) {
    window.clearInterval(scheduler);
    scheduler = null;
  }
  void nextAudio;
}

export function setMusicMuted(m: boolean): void {
  muted = m;
  if (master) master.gain.value = muted ? 0 : 0.18;
  if (currentAudio) currentAudio.volume = muted ? 0 : effectiveVol();
}

export function isMusicMuted(): boolean {
  return muted;
}
